import React from "react";
import CasestudyTemplate, {
  Header,
  Introduction,
  Challenge,
  Body,
} from "../../../../templates/casestudyTemplate";
import Section from "../../../../components/section";
import Gallery from "../../../../components/gallery";

export default function IndexPage() {
  return (
    <CasestudyTemplate id="t2a">
      <Header id="t2a" />
      <Introduction id="t2a">
        <p>
          The T2A is a 2nd generation analog entertainment system that you’ll
          find in 85 hospitals across the UK. It offers phone and entertainment
          services to more than 5 million patients a year.
        </p>
        <p>
          Patients can buy a range of entertainment products including phone,
          television, movies, radio and Sky Sports channels.
        </p>
        <p>
          I worked with the provider to redesign the system with an emphasis on
          simplifying the outdated UI and improving the user experience as much
          as possible within the constraints of the legacy hardware and
          software.
        </p>
        <p>
          This product represented the 2nd largest product share for the
          provider but was their worst-performing product.
        </p>
      </Introduction>
      <Challenge id="t2a">
        <p>
          I needed to find out why it wasn’t performing and create an experience
          that provided value to the patients and improved revenue for the
          provider. One of the biggest constraints were hardware limitations.
          T2A’s run on a version of IE6 with very limited CSS support and no
          JavaScript. The hardware is also very underpowered and as new services
          are added, speed takes a hit.
        </p>
        <p>
          I knew there were fundamental usability issues that could be resolved
          that wouldn’t require a complete rewrite of the software.
        </p>
      </Challenge>
      <Body id="t2a">
        <Section>
          <h3>Discovering the problems</h3>
          <p>
            I explored the pain points of using the current system through a
            combination of expert reviews and evaluations against usability best
            practises. Note. I was unable to conduct usability testing or user
            interviews due to patient confidentiality and NDA’s with the
            hospitals we worked with.
          </p>
          <p className="italic">
            Any designer will know that this isn’t ideal and I should stress the
            importance of actually talking to your users…but when dealing with
            organisations with a lower design maturity, it’s all about picking
            your battles and winning allies.
          </p>
        </Section>
        <Gallery
          id={"t2a"}
          images={[
            {
              source: "t2a_desk.jpg",
              alt: "",
            },
          ]}
          options={{ grid: 1, width: "inline", includeBackground: false }}
        />
        <Section>
          <p>The pain points I identified were:</p>
          <ul>
            <li>
              <strong>Having to create an account to use the product</strong>
            </li>
            <li>
              <strong>Inconsistent input & navigation</strong> - Navigation
              utilised a remote control connected to the device. However, there
              were several navigation models in use and none of them were
              consistent with other common remote control based experiences -
              your TV for example. Some screens used the numbers 0-9 to select
              menu options, others used coloured buttons. Some used up, down,
              left, and right arrows, but not in a selection method, rather,
              press left for TV, right for Movies and so on This kind of
              experience creep is common when a product lives beyond its ‘use by
              date’ and has been worked on by many developers over the years
              without product or design guidance.
            </li>
            <li>
              <strong>Slow performance</strong> - Navigating the product was a
              painful experience on top of the navigation issues I mentioned
              above. There was a significant delay when choosing menu options or
              loading apps like the TV service. Partly due to age but also
              impart due to the design of the interface.
            </li>
            <li>
              Heavy graphics were used to promote the services and the deals on
              offer and these needed to be downloaded and rendered when each
              screen loaded. Think back to the internet of old and you’ll start
              to empathise with these patients lumbered with outdated tech who
              just want to just catch up on Eastenders.
            </li>
          </ul>
          <h3>
            Solving the biggest problems first - Account creation and navigation
          </h3>
          <p>
            A huge win in this project was convincing the supplier to allow us
            to shift the account creation process from the start of use to the
            point of purchase. This allowed patients (the potential customers)
            to browse the system and see what was on offer before committing to
            creating an account and buying credit. A no brainer really but when
            revenue streams are on the line, a change like this becomes a very
            political issue.
          </p>
        </Section>
        <Gallery
          id={"t2a"}
          images={[
            {
              source: "t2a_flow.jpg",
              alt: "",
            },
          ]}
          options={{ grid: 1, width: "inline", includeBackground: false }}
        />
        <Section>
          <p>
            Fixing the navigation issues wasn’t too much trouble. After
            evaluating all of the conceptual models in use, we decided to scrap
            every single one and start again.
          </p>
          <p>
            Opting for a known model was the best thing to do even though this
            required the redesign of all of the menus and every single user
            interaction.
          </p>
          <p>
            The model we opted for is the same one you use whether you’re
            browsing Netflix, or scrolling through the TV guide - A menu option
            is pre-selected on page load and left is left, right is right, up is
            up, and down is down. It’s the most natural navigation model in use
            today.
          </p>
          <p className="italic">
            They probably didn't need a designer to tell them that but they
            needed a designer to look at the product holistically and highlight
            just how bad it had gotten in places.
          </p>

          <h3>Speeding up the slow performance</h3>
          <p>
            To solve this conundrum, I paired with an engineer to trial and
            error various methods for displaying content. Assets still needed to
            be images as the hardware had limited css support but we were able
            to find the balance between image quality and file size.
          </p>
          <p>
            In the end we opted for a handful of image sprites - a single image
            that contained all graphical assets that were to be presented on a
            page. This single image, only needing to be downloaded once
            increased performance in some areas by 50%.
          </p>
          <h3>Visual design</h3>
          <p>
            As with any major redesign, I took the opportunity to update the
            visual style, simplifying the menus and interactions to complement
            the new navigation model.
          </p>
        </Section>
        <Gallery
          id={"t2a"}
          images={[
            {
              source: "T2_1.png",
              alt: "",
            },
            {
              source: "T2_2.png",
              alt: "",
            },
            {
              source: "T2_3.png",
              alt: "",
            },
            {
              source: "T2_4.png",
              alt: "",
            },
            {
              source: "T2_5.png",
              alt: "",
            },
          ]}
          options={{ grid: 2, width: "full", includeBackground: true }}
        />
      </Body>
    </CasestudyTemplate>
  );
}
